import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatutCommande, statutColors, statutLabels } from '../../../mocks/commandes';
import { api } from '../../../services/api';

interface ProductionTabProps {
  userRole: string;
  userNom: string;
}

const COLONNES: StatutCommande[] = ['EN_ATTENTE', 'EN_COUPE', 'EN_COUTURE', 'TERMINE'];
const COLONNE_COLORS: Record<string, string> = {
  EN_ATTENTE: 'border-t-yellow-400',
  EN_COUPE: 'border-t-blue-400',
  EN_COUTURE: 'border-t-purple-400',
  TERMINE: 'border-t-foret-400',
};

export default function ProductionTab({ userRole, userNom }: ProductionTabProps) {
  const navigate = useNavigate();
  const [commandes, setCommandes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterMine, setFilterMine] = useState(userRole === 'coupeur' || userRole === 'tailleur');

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      try {
        const data = await api.getCommandes(token);
        setCommandes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const mesTaches = commandes.filter(c => {
    if (userRole === 'coupeur') return String(c.coupeur_id) === String(userNom) || c.statut === 'EN_ATTENTE';
    if (userRole === 'tailleur') return String(c.tailleur_id) === String(userNom) || c.statut === 'EN_COUPE';
    return true;
  });

  const displayCommandes = filterMine ? mesTaches : commandes;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-gray-600">Afficher :</span>
        <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-1">
          <button
            onClick={() => setFilterMine(false)}
            className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${!filterMine ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Toute la production
          </button>
          <button
            onClick={() => setFilterMine(true)}
            className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${filterMine ? 'bg-foret-500 text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Mes tâches
          </button>
        </div>
        <span className="text-xs text-gray-400 ml-auto">{displayCommandes.length} commande{displayCommandes.length > 1 ? 's' : ''}</span>
      </div>

      {loading && <div className="py-20 text-center text-gray-400 text-sm">Chargement de la production...</div>}

      {/* Kanban */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {!loading && COLONNES.map(statut => {
          const cards = displayCommandes.filter(c => c.statut === statut);
          return (
            <div key={statut} className={`bg-white rounded-2xl border border-gray-100 border-t-4 ${COLONNE_COLORS[statut]} flex flex-col`}>
              <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
                <span className="text-xs font-bold text-gray-700">{statutLabels[statut]}</span>
                <span className={`text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold ${statutColors[statut]}`}>{cards.length}</span>
              </div>
              <div className="flex-1 p-3 space-y-2 overflow-y-auto max-h-96">
                {cards.length === 0 && (
                  <div className="text-center py-8 text-gray-300">
                    <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1">
                      <i className="ri-inbox-line text-xl" />
                    </div>
                    <p className="text-xs">Aucune</p>
                  </div>
                )}
                {cards.map(c => (
                  <div
                    key={c.id}
                    onClick={() => navigate(`/commande/${c.id}`)}
                    className="bg-gray-50 rounded-xl p-3 cursor-pointer hover:bg-foret-50 transition-colors border border-gray-100"
                  >
                    <div className="flex items-start justify-between gap-1 mb-1.5">
                      <span className="text-xs font-mono text-foret-600 font-bold">{c.id}</span>
                      {c.tissuClient && (
                        <span className="text-xs bg-terre-100 text-terre-700 px-1.5 rounded-full flex-shrink-0">Tissu client</span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight truncate">{c.client_nom}</p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{c.modele_nom || 'Sans modèle'}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-calendar-line" />
                        </div>
                        {c.date_livraison ? new Date(c.date_livraison).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—'}
                      </div>
                      {(c.coupeur || c.tailleur) && (
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-user-line" />
                          </div>
                          <span className="truncate max-w-16">{c.coupeur || c.tailleur}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
